import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { ArrowLeft, MapPin, HandHeart, Package, Calendar } from '@phosphor-icons/react';
import Navbar from '@/components/custom/Navbar';
import Footer from '@/components/custom/Footer';
import ProductCard from '@/components/custom/ProductCard';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ArtisanDetailPage = () => {
  const { id } = useParams();
  const [artisan, setArtisan] = useState(null);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [artisanRes, productsRes] = await Promise.all([
          axios.get(`${API}/artisans/${id}`),
          axios.get(`${API}/products?artisan_id=${id}`)
        ]);
        setArtisan(artisanRes.data);
        setProducts(productsRes.data);
      } catch (error) {
        console.error('Error fetching artisan:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [id]);

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar />
        <div className="pt-24 max-w-7xl mx-auto px-6 lg:px-12">
          <div className="h-[400px] bg-muted rounded-xl animate-pulse mb-8" />
        </div>
      </div>
    );
  }

  if (!artisan) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar />
        <div className="pt-24 max-w-7xl mx-auto px-6 lg:px-12 text-center py-24">
          <h1 className="text-2xl font-medium mb-4">Artisan not found</h1>
          <Link to="/artisans">
            <Button variant="outline">Back to Artisans</Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      
      <main className="pt-20">
        {/* Back Link */}
        <div className="max-w-7xl mx-auto px-6 lg:px-12 py-4">
          <Link to="/artisans" className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors">
            <ArrowLeft className="w-4 h-4" />
            Back to Artisans
          </Link>
        </div>

        {/* Hero Section */}
        <section className="max-w-7xl mx-auto px-6 lg:px-12 mb-16" data-testid="artisan-detail">
          <div className="relative rounded-2xl overflow-hidden">
            <img
              src={artisan.image_url || 'https://images.pexels.com/photos/32160894/pexels-photo-32160894.jpeg'}
              alt={artisan.name}
              className="w-full h-[400px] md:h-[500px] object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#2C3E2D]/95 via-[#2C3E2D]/40 to-transparent" />
            
            <div className="absolute bottom-0 left-0 right-0 p-8 md:p-12 text-white">
              <Badge variant="secondary" className="mb-4 bg-white/20 border-white/30">
                {artisan.craft_specialty}
              </Badge>
              <h1 className="text-4xl md:text-5xl font-medium mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
                {artisan.name}
              </h1>
              <div className="flex flex-wrap items-center gap-6 text-white/80">
                <div className="flex items-center gap-2">
                  <MapPin weight="duotone" className="w-5 h-5" />
                  <span>{artisan.location}</span>
                </div>
                <div className="flex items-center gap-2">
                  <HandHeart weight="duotone" className="w-5 h-5" />
                  <span>{artisan.years_experience} years experience</span>
                </div>
                <div className="flex items-center gap-2">
                  <Package weight="duotone" className="w-5 h-5" />
                  <span>{artisan.products_created} products created</span>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Story Section */}
        <section className="max-w-7xl mx-auto px-6 lg:px-12 mb-16">
          <div className="max-w-3xl">
            <h2 className="text-2xl font-medium mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
              The Story
            </h2>
            <p className="text-lg text-muted-foreground leading-relaxed">
              {artisan.story}
            </p>
          </div>
        </section>

        {/* Products Section */}
        {products.length > 0 && (
          <section className="max-w-7xl mx-auto px-6 lg:px-12 pb-24">
            <h2 className="text-2xl font-medium mb-8" style={{ fontFamily: 'Playfair Display, serif' }}>
              Products by {artisan.name}
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {products.map(product => (
                <ProductCard key={product.id} product={product} />
              ))}
            </div>
          </section>
        )}
      </main>

      <Footer />
    </div>
  );
};

export default ArtisanDetailPage;
